import type {} from "@shopify/polaris-types";
import classNames from "classnames";
import { useId, type ComponentProps, type FocusEventHandler, type ReactNode } from "react";
import "../../tokens/tokens.css";
import "./style.css";

export type SChoiceListProValue = string | number | boolean;

export type SChoiceListProOption<T extends SChoiceListProValue = string> = {
  value: T;
  label: ReactNode;
  details?: ReactNode;
  disabled?: boolean;
};

export type SChoiceListProProps<T extends SChoiceListProValue = string> = {
  label: ReactNode;
  labelAccessibilityVisibility?: "visible" | "exclusive";
  name?: string;
  options: readonly SChoiceListProOption<T>[];
  /** Single selection uses the first value; multiple selection uses all values. */
  values: readonly T[];
  multiple?: boolean;
  /** 选项怎么排，跟 label 无关。 */
  direction?: "inline" | "block";
  /** label 跟选项的排布：vertical = 上下堆叠（默认），horizontal = 左右并排。 */
  labelDirection?: "horizontal" | "vertical";
  /** Applies to the inline layout; Shopify owns spacing in the block layout. */
  gap?: ComponentProps<"s-stack">["gap"];
  disabled?: boolean;
  details?: ReactNode;
  error?: ReactNode;
  onChange: (values: T[]) => void;
  onBlur?: FocusEventHandler<HTMLFieldSetElement>;
};

function choiceValue(value: SChoiceListProValue) {
  return `${typeof value}:${String(value)}`;
}

/** Shopify choices in block layouts, native inputs with custom styling in inline layouts. */
export function SChoiceListPro<T extends SChoiceListProValue>({
  label,
  labelAccessibilityVisibility = "visible",
  name,
  options,
  values,
  multiple = false,
  direction = "block",
  labelDirection = "vertical",
  gap = "small",
  disabled = false,
  details,
  error,
  onChange,
  onBlur,
}: SChoiceListProProps<T>) {
  const id = useId();
  const describedBy = [details ? `${id}-details` : "", error ? `${id}-error` : ""]
    .filter(Boolean)
    .join(" ");
  // label 与选项左右并排。exclusive 时 label 本来就不显示，排布无从谈起。
  const horizontal =
    labelDirection === "horizontal" && labelAccessibilityVisibility !== "exclusive";
  // 水平排布时可见 label 补个冒号；读屏用的那一份不带。
  const visibleLabel = label ? <>{label}:</> : label;

  if (direction === "block") {
    // 水平排布时 s-choice-list 自己那份 label 收进读屏，视觉位置交给外面这一行。
    const choiceList = (
      <s-choice-list
        label={label}
        labelAccessibilityVisibility={horizontal ? "exclusive" : labelAccessibilityVisibility}
        name={name ?? id}
        multiple={multiple}
        disabled={disabled}
        details={details}
        error={error}
        values={(multiple ? values : values.slice(0, 1)).map(choiceValue)}
        onChange={(event) => {
          if (disabled) return;
          const selected = event.currentTarget.values;
          const next = options
            .filter((option) =>
              multiple && option.disabled
                ? values.includes(option.value)
                : selected.includes(choiceValue(option.value)) &&
                  (!option.disabled || values.includes(option.value)),
            )
            .map((option) => option.value);
          onChange(multiple ? next : next.slice(0, 1));
        }}
      >
        {options.map((option) => (
          <s-choice
            key={choiceValue(option.value)}
            value={choiceValue(option.value)}
            disabled={option.disabled ?? false}
          >
            {option.label}
            {option.details && <s-text slot="details">{option.details}</s-text>}
          </s-choice>
        ))}
      </s-choice-list>
    );
    return (
      <fieldset
        className={classNames(
          "m-0 min-w-0 border-0 p-0 font-polar text-polar-body text-polar-text",
          {
            // Polaris 的 .choice-list-label 即使隐藏也带着 4px 下边距，这里用负边距抵消。
            "-mt-1": labelAccessibilityVisibility === "exclusive" || horizontal,
          },
        )}
        role="presentation"
        onBlur={onBlur}
      >
        {horizontal ? (
          <div className="flex items-center gap-3">
            {/* 纯视觉标签：可访问名仍由 s-choice-list 那份 exclusive 的 label 提供。 */}
            <span aria-hidden="true" className="shrink-0 p-0 font-polar-medium">
              {visibleLabel}
            </span>
            <div className="min-w-0 flex-1">{choiceList}</div>
          </div>
        ) : (
          choiceList
        )}
      </fieldset>
    );
  }

  const content = (
    <fieldset
      className="m-0 min-w-0 border-0 p-0 font-polar text-polar-body text-polar-text"
      role={multiple ? "group" : "radiogroup"}
      disabled={disabled}
      aria-labelledby={`${id}-label`}
      aria-describedby={describedBy || undefined}
      aria-invalid={error ? true : undefined}
      onBlur={onBlur}
    >
      {horizontal ? (
        // 可见 label 挪到了 fieldset 外面（见下方 return），这里只留 aria-labelledby 指过来的一份。
        <span id={`${id}-label`} className="sr-only">
          {label}
        </span>
      ) : (
        <legend
          id={`${id}-label`}
          className={classNames({
            "sr-only": labelAccessibilityVisibility === "exclusive",
            "mb-2": labelAccessibilityVisibility !== "exclusive",
            "p-0": labelAccessibilityVisibility !== "exclusive",
            "font-polar-medium": labelAccessibilityVisibility !== "exclusive",
          })}
        >
          {label}
        </legend>
      )}
      <s-stack direction={direction} gap={gap} alignItems="start">
        {options.map((option, index) => {
          const checked = multiple
            ? values.includes(option.value)
            : values.length > 0 && Object.is(values[0], option.value);
          const inactive = disabled || option.disabled;
          return (
            <label
              key={`${typeof option.value}:${String(option.value)}`}
              className="inline-flex min-w-0 cursor-pointer items-start gap-2 py-1 has-disabled:cursor-default has-disabled:opacity-50"
            >
              <span
                className={classNames("relative inline-flex shrink-0 items-center", {
                  "size-5": multiple,
                  "h-5": !multiple,
                  "w-4": !multiple,
                })}
              >
                <input
                  className="polar-choice-list-pro__input absolute inset-0 m-0 size-full cursor-inherit opacity-0 forced-colors:opacity-100"
                  type={multiple ? "checkbox" : "radio"}
                  name={name ?? id}
                  value={String(option.value)}
                  checked={checked}
                  disabled={inactive}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={
                    [describedBy, option.details ? `${id}-option-${index}` : ""]
                      .filter(Boolean)
                      .join(" ") || undefined
                  }
                  onChange={(event) => {
                    if (inactive) return;
                    if (!multiple) {
                      if (event.currentTarget.checked) onChange([option.value]);
                      return;
                    }
                    onChange(
                      event.currentTarget.checked
                        ? [
                            ...values.filter((value) => !Object.is(value, option.value)),
                            option.value,
                          ]
                        : values.filter((value) => !Object.is(value, option.value)),
                    );
                  }}
                />
                <span
                  className={classNames(
                    "polar-choice-list-pro__indicator pointer-events-none box-border grid shrink-0 place-items-center rounded border border-polar-disabled bg-polar-inverse forced-colors:invisible",
                    {
                      "size-full": multiple,
                      "size-4": !multiple,
                    },
                  )}
                  aria-hidden="true"
                />
              </span>
              <span className="min-w-0 wrap-anywhere">
                <s-text>{option.label}</s-text>
                {option.details && (
                  <span id={`${id}-option-${index}`} className="mt-1 block">
                    <s-text color="subdued">{option.details}</s-text>
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </s-stack>
      {details && (
        <div id={`${id}-details`} className="mt-1 block">
          <s-text color="subdued">{details}</s-text>
        </div>
      )}
      {error && (
        <div id={`${id}-error`} className="mt-1 block">
          <s-text tone="critical">{error}</s-text>
        </div>
      )}
    </fieldset>
  );

  if (!horizontal) return content;
  // label 放 fieldset 外面：<legend> 必须是 fieldset 的第一个子元素，没法跟内容并排。
  // aria-hidden 是因为可访问名由 fieldset 里那份 sr-only 的 span 提供。
  return (
    <div className="flex items-center gap-3">
      <span aria-hidden="true" className="shrink-0 p-0 font-polar-medium">
        {visibleLabel}
      </span>
      <div className="min-w-0 flex-1">{content}</div>
    </div>
  );
}
